import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { format } from 'date-fns';
import PropTypes from 'prop-types';

import { config } from '../../api/config';
import { actions } from '../../state';
import { serviceTypes } from '../../utils';
import { ALL_SERVICES_VALUE } from './discounts.data';
import {
  Header,
  Breadcrumb,
  Paper,
  PaperHeader,
  TitleSecondary,
} from '../../components';
import Form from './discounts.form';
import { validationErrors } from './discounts.validation';

const BANNER_SIMPLE_TYPE = 652;

const normalizeClientGroupsForSubmit = (
  selectedGroups = [],
  availableClientGroups = []
) => {
  if (!selectedGroups.length) {
    return null;
  }

  const availableGroupIds = availableClientGroups.map(group => group.id);

  if (
    availableGroupIds.length &&
    selectedGroups.length === availableGroupIds.length &&
    selectedGroups.every(groupId => availableGroupIds.includes(groupId))
  ) {
    return null;
  }

  return selectedGroups;
};

const normalizeLocationsForSubmit = (
  selectedLocations = [],
  availableLocations = []
) => {
  if (!selectedLocations.length) {
    return null;
  }

  const availableLocationIds = availableLocations.map(location => location.id);

  if (
    availableLocationIds.length &&
    selectedLocations.length === availableLocationIds.length &&
    selectedLocations.every(locationId =>
      availableLocationIds.includes(locationId)
    )
  ) {
    return null;
  }

  return selectedLocations;
};

const normalizeServiceTypesForSubmit = selectedServiceTypes => {
  if (!selectedServiceTypes?.length) {
    return null;
  }

  if (
    serviceTypes.every(serviceType =>
      selectedServiceTypes.includes(serviceType)
    )
  ) {
    return null;
  }

  return selectedServiceTypes;
};

const AddDiscount = ({
  dispatch,
  history,
  discounts: { loading },
  clientGroups,
  availableLocations,
}) => {
  const [values, setValues] = useState({
    code: '',
    title_lt: '',
    title_en: '',
    title_ru: '',
    description_lt: '',
    description_en: '',
    description_ru: '',
    dateFrom: format(Date.now(), 'yyyy-MM-dd'),
    dateTo: format(Date.now(), 'yyyy-MM-dd'),
    timeFrom: '',
    timeTo: '',
    type: 191,
    usage_limit: 1,
    createDiscountBanner: false,
    bannerPlacements: [],
    announcement: {
      type: BANNER_SIMPLE_TYPE,
      title_lt: '',
      title_en: '',
      title_ru: '',
      description_lt: '',
      description_en: '',
      description_ru: '',
      banner: null,
      banner_id: 0,
      deep_link: '',
    },
  });
  const [locations, setLocations] = useState([]);
  const [services, setServices] = useState([]);
  const [weekdays, setWeekdays] = useState([]);
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    dispatch(actions.discounts.setDiscountBannerAction({}));
  }, [dispatch]);

  const handleBannerChange = useCallback(banner => {
    setValues(currentValues => ({
      ...currentValues,
      announcement: {
        ...currentValues.announcement,
        banner: banner || null,
        banner_id: banner?.id || 0,
      },
    }));
  }, []);

  const handleInputChange = ({ target: { name, value } }) => {
    if (name.includes('.')) {
      const splitName = name.split('.');
      setValues({
        ...values,
        announcement: { ...values.announcement, [splitName[1]]: value },
      });
    } else setValues({ ...values, [name]: value });
  };

  const setCreateDiscountBanner = ({ target: { checked } }) => {
    setValues({
      ...values,
      createDiscountBanner: checked,
    });
  };

  const handleSubmit = e => {
    e.preventDefault();

    const error = validationErrors(values, services);

    if (error) {
      dispatch(actions.error.setErrorAction({ msg: error }));
    } else {
      const codeToSubmit = values.code;
      const titleLtToSubmit = values.title_lt;
      const titleEnToSubmit = values.title_en;
      const titleRuToSubmit = values.title_ru;
      const descriptionLtToSubmit = values.description_lt;
      const descriptionEnToSubmit = values.description_en;
      const descriptionRuToSubmit = values.description_ru;
      const selectedServices = services.filter(service => {
        const serviceType = service?.serviceType;

        return (
          serviceType !== '' &&
          serviceType !== null &&
          serviceType !== undefined
        );
      });
      const isAllServicesSelected =
        selectedServices.length === 1 &&
        selectedServices[0]?.serviceType === ALL_SERVICES_VALUE;
      const serviceDiscountsToSubmit = isAllServicesSelected
        ? null
        : selectedServices.map(service => ({
            service_type: Number(service.serviceType),
            discount_percent: Number(service.discount),
          }));
      const dateFrom = new Date(values.dateFrom).toISOString();
      const dateTo = new Date(values.dateTo).toISOString();
      const dateIntervalToSubmit = [dateFrom, dateTo];
      const locationsToSubmit = normalizeLocationsForSubmit(
        locations,
        availableLocations
      );
      const serviceTypesToSubmit = isAllServicesSelected
        ? null
        : normalizeServiceTypesForSubmit(
            selectedServices.map(service => Number(service.serviceType))
          );
      const weekdaysToSubmit = weekdays.length ? weekdays : null;
      const groupsToSubmit = normalizeClientGroupsForSubmit(
        groups,
        clientGroups
      );
      const timeToSubmit =
        values.timeFrom === values.timeTo
          ? null
          : [values.timeFrom, values.timeTo];
      const isMultiUse = Number(values.type) === 192;

      const body = {
        code: codeToSubmit,
        description_lt: descriptionLtToSubmit,
        description_en: descriptionEnToSubmit,
        description_ru: descriptionRuToSubmit,
        date_interval: dateIntervalToSubmit,
        ...(isAllServicesSelected
          ? {
              discount: Number(selectedServices[0].discount),
              service_discounts: null,
              service_types: null,
            }
          : {
              service_discounts: serviceDiscountsToSubmit,
              service_types: serviceTypesToSubmit,
            }),
        client_groups: groupsToSubmit,
        locations: locationsToSubmit,
        time_interval: timeToSubmit,
        week_days: weekdaysToSubmit,
        type: Number(values.type),
        usage_limit: isMultiUse ? Number(values.usage_limit) : null,
        createDiscountBanner: values.createDiscountBanner,
        announcement: values.createDiscountBanner
          ? {
              type: values.announcement.type || BANNER_SIMPLE_TYPE,
              title_lt: titleLtToSubmit,
              title_en: titleEnToSubmit,
              title_ru: titleRuToSubmit,
              description_lt: descriptionLtToSubmit,
              description_en: descriptionEnToSubmit,
              description_ru: descriptionRuToSubmit,
              app_notification: false,
              app_notification_send_at: null,
              deep_link: values.announcement.deep_link,
              placements: values.bannerPlacements,
            }
          : null,
      };

      dispatch(actions.discounts.addDiscountAction(body, history));
    }
  };

  return (
    <section>
      <Header size="sm">
        <Breadcrumb
          title={config.PAGES.discounts}
          baseUrl={config.APP_PATHS.discounts}
          variant="pageTitle"
        />
      </Header>

      <Paper size="sm">
        <PaperHeader>
          <TitleSecondary>Pridėti nuolaidą</TitleSecondary>
        </PaperHeader>
        <Form
          values={values}
          onChange={handleInputChange}
          onSubmit={handleSubmit}
          onBannerChange={handleBannerChange}
          loading={loading}
          weekdays={weekdays}
          setWeekdays={setWeekdays}
          services={services}
          setServices={setServices}
          locations={locations}
          setLocations={setLocations}
          groups={groups}
          setGroups={setGroups}
          setCreateDiscountBanner={setCreateDiscountBanner}
        />
      </Paper>
    </section>
  );
};

const mapStateToProps = state => ({
  discounts: state.discounts,
  clientGroups: state.clientGroups.groups,
  availableLocations: state.locations.locations,
});

AddDiscount.propTypes = {
  dispatch: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  discounts: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
  }).isRequired,
  clientGroups: PropTypes.arrayOf(PropTypes.object).isRequired,
  availableLocations: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default connect(mapStateToProps)(AddDiscount);
